import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getProject } from '../data/projects';
import { buildArchive } from '../data/documents';
import './DocumentArchive.css';

// Format any of the date shapes we collect: ISO (uploads/plans) or handasa's
// "D/M/YYYY" (building file) → DD.MM.YY.
function fmtAny(d) {
  if (!d) return '';
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(d);
  if (m) {
    const yr = m[3].length === 2 ? `19${m[3]}` : m[3];
    return `${m[1].padStart(2, '0')}.${m[2].padStart(2, '0')}.${yr.slice(2)}`;
  }
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}.${String(dt.getFullYear()).slice(2)}`;
}

// Project-scoped document archive: every document collected for the project
// (uploads, תיק בניין, plans) in a searchable drawer. Rendered either as a rail
// card (`inline`, spec 2.4) or a floating corner button. `refreshSignal` bumps
// the fetch when the caller's document set changes (e.g. a gap upload).
export default function DocumentArchive({ projectId, inline = false, refreshSignal }) {
  const [open, setOpen] = useState(false);
  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!projectId) { setArchive(null); return undefined; }
    let alive = true;
    setLoading(true);
    getProject(projectId)
      .then((loaded) => { if (alive) setArchive(buildArchive(loaded)); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, refreshKey, refreshSignal]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!projectId) return null;

  const openDrawer = () => { setOpen(true); setRefreshKey((k) => k + 1); };
  const total = archive?.total || 0;
  const q = query.trim();
  const groups = (archive?.groups || []).map((g) => ({
    ...g,
    docs: q ? g.docs.filter((d) => `${d.name} ${d.meta || ''} ${d.ref || ''}`.includes(q)) : g.docs,
  }));
  const shown = groups.reduce((n, g) => n + g.docs.length, 0);

  return (
    <>
      {inline ? (
        <button type="button" className="da-railcard" onClick={openDrawer} aria-label="ארכיון מסמכים">
          <span className="da-railcard-ic"><i className="fi fi-rr-folder-open" /></span>
          <span className="da-railcard-text">
            <span className="da-railcard-title">ארכיון מסמכים</span>
            <span className="da-railcard-sub">{total > 0 ? `${total} מסמכים בפרויקט` : 'עדיין אין מסמכים'}</span>
          </span>
          <i className="fi fi-rr-angle-small-left da-railcard-chev" />
        </button>
      ) : (
        <button type="button" className="da-fab" onClick={openDrawer} title="ארכיון מסמכים" aria-label="ארכיון מסמכים">
          <i className="fi fi-rr-folder-open" />
          <span className="da-fab-label">ארכיון מסמכים</span>
          {total > 0 && <span className="da-fab-count">{total}</span>}
        </button>
      )}

      {/* Portal to <body>: the trigger lives inside the sticky rail, whose
          stacking context traps the backdrop's z-index — positioned elements
          elsewhere on the page (field action buttons) then paint ABOVE the
          dimmer. Escaping the ancestor context fixes the "glowing buttons". */}
      {open && createPortal(
        <div className="da-backdrop" onMouseDown={() => setOpen(false)}>
          <aside className="da-panel" dir="rtl" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="ארכיון מסמכים">
            <header className="da-head">
              <div className="da-head-titles">
                <h2><i className="fi fi-rr-folder-open" /> ארכיון מסמכים</h2>
                <span className="da-head-sub">{total} מסמכים בפרויקט</span>
              </div>
              <button type="button" className="da-x" onClick={() => setOpen(false)} aria-label="סגור"><i className="fi fi-rr-cross-small" /></button>
            </header>

            <div className="da-search">
              <i className="fi fi-rr-search" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="חיפוש מסמך…" />
              {query && <button type="button" className="da-search-clear" onClick={() => setQuery('')} aria-label="נקה"><i className="fi fi-rr-cross-small" /></button>}
            </div>

            <div className="da-body">
              {loading && !archive && <div className="da-loading"><span className="rp-spinner" /> טוען מסמכים…</div>}
              {!loading && total === 0 && <div className="da-empty"><i className="fi fi-rr-folder" /> עדיין אין מסמכים בפרויקט.</div>}
              {!!total && q && shown === 0 && <div className="da-empty">לא נמצאו מסמכים תואמים.</div>}
              {groups.map((g) => (g.docs.length > 0 ? (
                <section className="da-group" key={g.key}>
                  <div className="da-group-head">
                    <span className="da-group-title"><i className={`fi ${g.icon}`} /> {g.label}</span>
                    <span className="da-group-count">{g.docs.length}</span>
                    {g.link && (
                      <a className="da-group-link" href={g.link} target="_blank" rel="noreferrer">
                        {g.linkLabel} <i className="fi fi-rr-arrow-up-right-from-square" />
                      </a>
                    )}
                  </div>
                  {/* Column headers (graphic-log §3.1) — every column named so the
                      user knows what each cell represents. */}
                  <ul className="da-list da-list-table">
                    <li className="da-doc da-doc-headrow" aria-hidden="true">
                      <span className="da-col-name">שם המסמך</span>
                      <span className="da-col-meta">סוג / פרטים</span>
                      <span className="da-col-date">תאריך</span>
                      <span className="da-col-status">סטטוס סוכן</span>
                      <span className="da-col-action">פעולה</span>
                    </li>
                    {g.docs.map((d) => (
                      <li className="da-doc" key={d.id}>
                        <span className="da-col-name" title={d.name}>
                          <i className="fi fi-rr-document da-doc-ic" />
                          <span className="da-doc-name">{d.ref ? `${d.ref} · ${d.name}` : d.name}</span>
                        </span>
                        <span className="da-col-meta">{d.meta || '—'}</span>
                        <span className="da-col-date">{fmtAny(d.date) || '—'}</span>
                        {/* Agent-read status (spec 2.3) — when the agent last read it. */}
                        <span className={`da-col-status da-doc-read ${d.agentReadAt ? 'is-read' : ''}`}>
                          <i className={`fi ${d.agentReadAt ? 'fi-rr-check-circle' : 'fi-rr-clock'}`} />
                          {d.agentReadAt ? `נקרא · ${fmtAny(d.agentReadAt)}` : 'טרם נקרא'}
                        </span>
                        <span className="da-col-action">
                          {d.link ? (
                            <a className="da-doc-open" href={d.link} target="_blank" rel="noreferrer" title="פתח מסמך">
                              <i className="fi fi-rr-arrow-up-right-from-square" /> פתיחה
                            </a>
                          ) : <span className="da-col-none">—</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null))}
            </div>
          </aside>
        </div>,
        document.body
      )}
    </>
  );
}
